// Loads Google's reCAPTCHA v2 script once, for explicit rendering. Generic:
// the /early-access form (ocl_online#170) and the on-site newsletter form
// (ocl_online#208) share it. The Brevo forms verify the token with the secret
// they hold; this side only ever sees the public site key.
const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';
const ONLOAD = '__oclRecaptchaOnload';

// reCAPTCHA's language codes; the site's zh is Simplified Chinese.
export const recaptchaLanguage = (language) => {
  const code = (language || 'en').split('-')[0];
  return code === 'zh' ? 'zh-CN' : code;
};

let pending = null;

// Resolves with grecaptcha once render() is usable. Rejects if the script
// errors (a content blocker, offline) or isn't ready within timeoutMs. A
// rejection isn't cached, so a later mount tries again.
export const loadRecaptcha = ({ language, timeoutMs = 10000, win = window, doc = document } = {}) => {
  if (win.grecaptcha && win.grecaptcha.render)
    return Promise.resolve(win.grecaptcha);
  if (pending)
    return pending;

  pending = new Promise((resolve, reject) => {
    const fail = (message) => {
      pending = null;
      reject(new Error(message));
    };
    const timer = setTimeout(() => fail('reCAPTCHA did not load in time'), timeoutMs);
    win[ONLOAD] = () => {
      clearTimeout(timer);
      resolve(win.grecaptcha);
    };
    const script = doc.createElement('script');
    script.src = `${SCRIPT_URL}?onload=${ONLOAD}&render=explicit&hl=${encodeURIComponent(recaptchaLanguage(language))}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      clearTimeout(timer);
      fail('reCAPTCHA script failed to load');
    };
    doc.head.appendChild(script);
  });
  return pending;
};

// Tests only: forget a load in flight.
export const resetRecaptchaLoader = () => {
  pending = null;
};
